// src/pages/Shop/ShopPage.js
//
// Public online voucher page (mobile-first, nicer desktop layout) with:
//  - Multivoucher-style denomination tiles with +/- controls.
//  - In-browser cart composing final voucher value from denominations.
//  - On desktop: ONLY the right column scrolls vertically (default mode).
//  - Merchant / payment / legal info displayed as accordions.
//
// IMPORTANT NOTE ABOUT "SHAKING / JITTER"
// --------------------------------------
// The typical cause is layout oscillation triggered by scrollbar appearance
// and disappearance. When a scrollbar toggles, the available width changes,
// text wraps differently, element heights change slightly, and your JS
// "smart fallback" may flip modes repeatedly -> visible jitter.
//
// This file implements TWO anti-jitter strategies:
//   1) CSS (paired with ShopPage.css):
//      - Desktop viewport always reserves scrollbar space (overflow-y: scroll).
//        This removes width changes when switching modes.
//
//   2) JS hysteresis + cooldown:
//      - We only switch modes when overflow is clearly present,
//      - and we don't allow rapid toggling within a short time window.
//
// IMPORTANT BUSINESS SCOPE
// ------------------------
// ShopPage is intentionally VOUCHER-ONLY UI.
//
// This is a very important boundary in the new architecture:
//
//   - ShopPage sells only vouchers
//   - ShopPage does NOT sell packages
//   - therefore ShopPage must remain aligned only with the
//     VOUCHER template family
//
// Even after backend PDF split:
//
//   backend/pdf/templates/voucher/*
//   backend/pdf/templates/package/*
//
// this page still belongs only to:
//
//   voucher family
//
// WHY THIS STILL MATTERS TO BACKEND PDF
// -------------------------------------
// Although ShopPage itself is voucher-only UI, the selected:
//
//   - templateType
//   - templateVersion
//
// are sent to backend during checkout and later influence which PDF template
// is used for the purchased voucher.
//
// So this page must stay aligned with:
//
//   - voucher template types exposed to online buyers
//   - voucher template versions exposed to online buyers
//   - voucher theme derivation used by preview
//
// BACKEND CONTRACT (UNCHANGED)
// ----------------------------
// createOrderAndInitPayment({
//   amount,
//   buyerEmail,
//   buyerName,
//   recipientName,
//   recipientEmail,
//   message,
//   templateType,
//   templateVersion,
// });
//
// IMPORTANT TEMPLATE-SPLIT DECISION
// ---------------------------------
// Because ShopPage is voucher-only:
//
//   - it should continue using voucherLayoutConfig.js
//   - it should NOT import packageLayoutConfig.js
//   - it should sanitize templateType/templateVersion against
//     VOUCHER-SIDE allowed values only
//
// In other words:
//   this file is not a mixed voucher/package page.
//   It remains a voucher-commerce page that must stay consistent
//   with voucher PDF family routing.
//

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchShopConfig,
  createOrderAndInitPayment,
} from "../../services/shopService";
import { VoucherPreview } from "../../components/VoucherPreview";
import PublicLanguageDropdown from "../../components/PublicLanguageDropdown";
import { normalizeLanguage } from "../../i18n";
import {
  getThemeForTemplateVersion,
} from "../../config/voucherLayoutConfig";
import "./ShopPage.css";

/**
 * Predefined voucher denominations (PLN) used for the quick-pick tiles.
 * Filtered against backend min/max at runtime.
 */
const PRESET_AMOUNTS = [100, 150, 200, 250, 300, 400, 500, 750, 1000];

/**
 * localStorage key for cart persistence.
 * Structure:
 *   { items: CartItem[], savedAt: number }
 */
const CART_STORAGE_KEY = "hb_shop_cart_v1";

/** Cart TTL: 1 hour */
const CART_TTL_MS = 60 * 60 * 1000;

/**
 * Optional key set by ThankYou page to indicate the last successful purchase time.
 * If the cart was saved before that time, we clear it to avoid confusion.
 */
const LAST_PURCHASE_AT_KEY = "hb_last_successful_purchase_at";

const PUBLIC_LANGUAGE_QUERY_PARAM = "lng";

function readPublicLanguageFromUrl() {
  if (typeof window === "undefined") return null;

  const raw = new URLSearchParams(window.location.search).get(
    PUBLIC_LANGUAGE_QUERY_PARAM
  );

  if (!raw) return null;

  const language = String(raw).trim().toLowerCase().split("-")[0];
  return language === "pl" || language === "en" ? language : null;
}

function buildPublicUrlWithLanguage(path, language) {
  const normalizedLanguage = normalizeLanguage(language);
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${PUBLIC_LANGUAGE_QUERY_PARAM}=${encodeURIComponent(
    normalizedLanguage
  )}`;
}

/**
 * Lightweight email validation for checkout UX.
 * Not RFC-perfect by design (we just catch obvious mistakes).
 */
const isValidEmail = (value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  return v.includes("@") && v.split("@")[1]?.includes(".");
};

/** Try to extract a helpful backend error message */
const extractBackendMessage = (err) => {
  const msg =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    null;

  return msg ? String(msg) : null;
};

// ---------------------------------------------------------------------------
// Feature flag: show/hide "Voucher type" section
// ---------------------------------------------------------------------------
//
// In CRA, env vars must start with REACT_APP_ to be available in frontend build.
// Defaults to true (show section) if env var is missing.
//
// IMPORTANT:
// Hiding this section does NOT mean templateType disappears from backend payload.
// It only means the user cannot change it manually, so we enforce a safe default.
const SHOP_SHOW_VOUCHER_TYPE =
  String(process.env.REACT_APP_SHOP_SHOW_VOUCHER_TYPE ?? "true")
    .toLowerCase()
    .trim() === "true";

// ---------------------------------------------------------------------------
// Voucher-only defaults for public online shop
// ---------------------------------------------------------------------------
//
// These defaults are intentionally VOUCHER-FAMILY defaults.
// ShopPage never chooses package defaults.
//
// IMPORTANT:
// templateVersion MUST remain user-selectable even if voucher type is hidden.
//
const DEFAULT_TEMPLATE_TYPE = "A4_ON_STATIONERY";
const DEFAULT_TEMPLATE_VERSION = "company";

// ---------------------------------------------------------------------------
// Voucher-only public shop catalogs
// ---------------------------------------------------------------------------
//
// IMPORTANT ARCHITECTURAL NOTE
// ----------------------------
// Backend may support more voucher template types than the public shop exposes.
// That is acceptable.
//
// Public shop should expose only the online-commerce-safe subset that the
// business actually wants customers to choose from.
//
// Currently exposed in ShopPage:
//   - A4_ON_STATIONERY
//   - A4_ENVELOPE
//   - A4_COMBINED
//
// Not exposed publicly (even if backend may support them):
//   - A4_FULL
//   - A4_PURE_CARD
//
// Why keep that distinction?
// --------------------------
// Because frontend public offering is a business/product choice,
// while backend router supports the full voucher family.
//
const SHOP_SUPPORTED_TEMPLATE_TYPES = [
  "A4_ON_STATIONERY",
  "A4_ENVELOPE",
  "A4_COMBINED",
];

const SHOP_SUPPORTED_TEMPLATE_VERSIONS = [
  "company",
  "christmas",
  "valentine",
];

/**
 * normalizeShopVoucherTemplateType(rawType)
 *
 * Sanitizes templateType for public voucher shop usage.
 *
 * Fallback:
 *   - DEFAULT_TEMPLATE_TYPE
 *
 * IMPORTANT:
 * Even if backend knows more voucher template types, ShopPage intentionally
 * restricts itself to the subset that is publicly offered online.
 */
function normalizeShopVoucherTemplateType(rawType) {
  if (!rawType) return DEFAULT_TEMPLATE_TYPE;

  const value = String(rawType).trim().toUpperCase();
  return SHOP_SUPPORTED_TEMPLATE_TYPES.includes(value)
    ? value
    : DEFAULT_TEMPLATE_TYPE;
}

/**
 * normalizeShopVoucherTemplateVersion(rawVersion)
 *
 * Sanitizes templateVersion for public voucher shop usage.
 *
 * Fallback:
 *   - DEFAULT_TEMPLATE_VERSION
 *
 * IMPORTANT:
 * ShopPage remains voucher-only, so only voucher motif keys are allowed here.
 */
function normalizeShopVoucherTemplateVersion(rawVersion) {
  if (!rawVersion) return DEFAULT_TEMPLATE_VERSION;

  const value = String(rawVersion).trim().toLowerCase();
  return SHOP_SUPPORTED_TEMPLATE_VERSIONS.includes(value)
    ? value
    : DEFAULT_TEMPLATE_VERSION;
}

const ShopPage = () => {
  const { t, i18n } = useTranslation("shop");

  // Public shop pages are anonymous pages, so their visible language follows
  // the current i18next runtime language. Unlike authenticated user settings,
  // this selector intentionally does not persist anything to the user profile.
  const currentPublicLanguage = normalizeLanguage(
    i18n.resolvedLanguage || i18n.language || "en"
  );

  useEffect(() => {
    // Public legal links can be opened in a new browser tab. Because the public
    // language policy intentionally avoids localStorage/sessionStorage, the
    // selected display language is propagated through a small, explicit query
    // parameter instead. This keeps anonymous pages deterministic without
    // turning language choice into a durable browser preference.
    const urlLanguage = readPublicLanguageFromUrl();
    if (urlLanguage && normalizeLanguage(i18n.language) !== urlLanguage) {
      i18n.changeLanguage(urlLanguage);
    }
  }, [i18n]);

  const handlePublicLanguageChange = (selection) => {
    if (!selection?.language) return;
    i18n.changeLanguage(selection.language);
  };

  // =========================================================================
  // CONFIG STATE (dynamic rules from backend)
  // =========================================================================
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [config, setConfig] = useState(null);

  // =========================================================================
  // CART STATE
  // =========================================================================
  const [cartItems, setCartItems] = useState([]);

  // =========================================================================
  // FORM STATE
  // =========================================================================
  const [amount, setAmount] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // =========================================================================
  // Voucher layout choices (voucher-only)
  // =========================================================================
  //
  // IMPORTANT:
  // These state values are intentionally voucher-family values only.
  // ShopPage does not support package motifs or package template families.
  //
  const [templateType, setTemplateType] = useState(DEFAULT_TEMPLATE_TYPE);
  const [templateVersion, setTemplateVersion] = useState(
    DEFAULT_TEMPLATE_VERSION
  );
  const [showPreview, setShowPreview] = useState(false);

  // Accordions
  const [showMerchantInfo, setShowMerchantInfo] = useState(false);
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);
  const [showLegalInfo, setShowLegalInfo] = useState(false);

  // =========================================================================
  // SMART DESKTOP SCROLL FALLBACK (JS-driven)
  // =========================================================================
  const [useGlobalScrollFallback, setUseGlobalScrollFallback] =
    useState(false);

  // =========================================================================
  // Paths / assets / internal legal pages
  // =========================================================================
  const appBasePath = process.env.PUBLIC_URL || "";
  const salonName = "HB Health & Beauty";
  const logoSrc = `${appBasePath}/hb_logo.png`;
  const voucherThemeSrc = `${appBasePath}/voucher_theme.png`;
  const cartIconSrc = `${appBasePath}/hb_cart.png`;

  const termsUrl = buildPublicUrlWithLanguage(
    `${appBasePath}/regulamin`,
    currentPublicLanguage
  );
  const privacyPolicyUrl = buildPublicUrlWithLanguage(
    `${appBasePath}/polityka-prywatnosci`,
    currentPublicLanguage
  );

  // =========================================================================
  // Refs
  // =========================================================================
  const viewportRef = useRef(null); // used for stable scrollbar behavior + CSS vars
  const orderColumnRef = useRef(null);
  const infoColumnRef = useRef(null);

  const amountSectionRef = useRef(null);
  const amountInputRef = useRef(null);

  const buyerEmailRef = useRef(null);
  const buyerNameRef = useRef(null);
  const recipientEmailRef = useRef(null);
  const acceptTermsRef = useRef(null);
  const acceptPrivacyRef = useRef(null);

  const fieldRefMap = useMemo(
    () => ({
      amount: amountSectionRef,
      buyerEmail: buyerEmailRef,
      buyerName: buyerNameRef,
      recipientEmail: recipientEmailRef,
      acceptTerms: acceptTermsRef,
      acceptPrivacy: acceptPrivacyRef,
    }),
    []
  );

  // Neutralize any global body scaling on the login screen only
  useEffect(() => {
    const prevTransform = document.body.style.transform;
    const prevWidth = document.body.style.width;
    document.body.style.transform = "none";
    document.body.style.width = "auto";
    return () => {
      document.body.style.transform = prevTransform;
      document.body.style.width = prevWidth;
    };
  }, []);

  // =========================================================================
  // Enforce defaults when voucher type section is hidden
  // =========================================================================
  //
  // If voucher-type UI is hidden, the public shop must behave deterministically
  // and always stay on the configured safe default.
  //
  useEffect(() => {
    if (SHOP_SHOW_VOUCHER_TYPE) return;
    setTemplateType(DEFAULT_TEMPLATE_TYPE);
  }, []);

  // =========================================================================
  // Anti-jitter helper: compute scrollbar width and store as CSS variable
  // =========================================================================
  //
  // Even though we also use CSS `overflow-y: scroll` on desktop (see CSS file),
  // this variable is useful if you later want to apply padding compensation
  // or debug scrollbar-related behavior.
  //
  useEffect(() => {
    if (typeof window === "undefined") return;

    const setVars = () => {
      const el = viewportRef.current;
      if (!el) return;

      // Classic scrollbar width measurement:
      // innerWidth includes scrollbar, clientWidth excludes it.
      const sbw =
        window.innerWidth - document.documentElement.clientWidth;
      el.style.setProperty(
        "--shop-scrollbar-width",
        `${Math.max(0, sbw)}px`
      );
    };

    setVars();
    window.addEventListener("resize", setVars);
    return () => window.removeEventListener("resize", setVars);
  }, []);

  // =========================================================================
  // SMART DESKTOP SCROLL MODE (ANTI-FLIP / ANTI-SHAKE VERSION)
  // =========================================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    const desktopMedia = window.matchMedia("(min-width: 992px)");
    let raf = 0;
    let ro = null;

    // Cooldown prevents rapid toggling when values hover around thresholds.
    const lastToggleAtRef = { current: 0 };
    const TOGGLE_COOLDOWN_MS = 900; // tuned to kill visible oscillation

    // Hysteresis thresholds:
    // - enable when overflow is clearly significant
    // - disable only when overflow is essentially gone
    const ENABLE_THRESHOLD_PX = 16;
    const DISABLE_THRESHOLD_PX = 2;

    const measure = () => {
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const isDesktop = desktopMedia.matches;

        // On mobile/tablet we keep default global scroll.
        if (!isDesktop) {
          setUseGlobalScrollFallback(false);
          return;
        }

        const leftCol = infoColumnRef.current;
        if (!leftCol) {
          setUseGlobalScrollFallback(false);
          return;
        }

        const visible = leftCol.clientHeight;
        const total = leftCol.scrollHeight;
        const overflowPx = total - visible;

        const now = Date.now();
        const inCooldown =
          now - lastToggleAtRef.current < TOGGLE_COOLDOWN_MS;

        setUseGlobalScrollFallback((prev) => {
          // If we're within cooldown, never toggle (prevents visible shaking).
          if (inCooldown) return prev;

          const shouldEnable = overflowPx > ENABLE_THRESHOLD_PX;
          const shouldDisable = overflowPx <= DISABLE_THRESHOLD_PX;

          if (!prev && shouldEnable) {
            lastToggleAtRef.current = now;
            return true;
          }

          if (prev && shouldDisable) {
            lastToggleAtRef.current = now;
            return false;
          }

          return prev;
        });
      });
    };

    const onResize = () => measure();
    const onMediaChange = () => measure();

    window.addEventListener("resize", onResize);

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener("change", onMediaChange);
    } else {
      desktopMedia.addListener(onMediaChange);
    }

    // ResizeObserver watches the LEFT column only. It can fire frequently,
    // but with cooldown + hysteresis we avoid mode flip oscillation.
    if (infoColumnRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => measure());
      ro.observe(infoColumnRef.current);
    }

    // Initial measure
    measure();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);

      if (desktopMedia.removeEventListener) {
        desktopMedia.removeEventListener("change", onMediaChange);
      } else {
        desktopMedia.removeListener(onMediaChange);
      }

      if (ro) ro.disconnect();
    };
  }, []);

  // =========================================================================
  // Load dynamic shop config from backend
  // =========================================================================
  useEffect(() => {
    let isMounted = true;

    setLoadingConfig(true);
    setConfigError(null);

    fetchShopConfig()
      .then((data) => {
        if (!isMounted) return;
        setConfig(data);
        setLoadingConfig(false);
      })
      .catch((err) => {
        console.error("[ShopPage] Error loading config:", err);
        if (!isMounted) return;

        setConfigError(
          t("errors.configLoad")
        );
        setLoadingConfig(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // =========================================================================
  // Cart: optionally clear via URL query param
  // =========================================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const url = new URL(window.location.href);
      const clearCart = url.searchParams.get("clearCart");
      const purchased = url.searchParams.get("purchased");

      if (clearCart === "1" || purchased === "1") {
        window.localStorage.removeItem(CART_STORAGE_KEY);
        window.localStorage.setItem(
          LAST_PURCHASE_AT_KEY,
          String(Date.now())
        );

        url.searchParams.delete("clearCart");
        url.searchParams.delete("purchased");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (err) {
      console.error(
        "[ShopPage] Failed to handle clearCart query param:",
        err
      );
    }
  }, []);

  // =========================================================================
  // Cart: restore from localStorage
  // =========================================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const savedAt = parsed?.savedAt || 0;
      const ageMs = Date.now() - savedAt;

      if (ageMs > CART_TTL_MS) {
        window.localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      const lastPurchaseAtRaw = window.localStorage.getItem(
        LAST_PURCHASE_AT_KEY
      );
      const lastPurchaseAt = lastPurchaseAtRaw
        ? Number(lastPurchaseAtRaw)
        : 0;

      if (lastPurchaseAt && savedAt && savedAt <= lastPurchaseAt) {
        window.localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      if (Array.isArray(parsed?.items) && parsed.items.length > 0) {
        setCartItems(parsed.items);
      }
    } catch (err) {
      console.error(
        "[ShopPage] Failed to restore cart from localStorage:",
        err
      );
    }
  }, []);

  // =========================================================================
  // Cart: persist
  // =========================================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      if (!cartItems || cartItems.length === 0) {
        window.localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }

      const payload = { items: cartItems, savedAt: Date.now() };
      window.localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify(payload)
      );
    } catch (err) {
      console.error(
        "[ShopPage] Failed to persist cart to localStorage:",
        err
      );
    }
  }, [cartItems]);

  // =========================================================================
  // Helpers: config values with defaults
  // =========================================================================
  const getMinAmount = () =>
    config?.minAmount != null ? config.minAmount : 50;
  const getMaxAmount = () =>
    config?.maxAmount != null ? config.maxAmount : 1000;
  const getStep = () => (config?.step != null ? config.step : 1);
  const getCurrency = () => config?.currency || "PLN";

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return `– ${getCurrency()}`;

    // Public shop display follows the currently selected public i18n language.
    // Currency business logic remains unchanged: backend still receives numeric
    // amounts and currency code separately.
    const locale = String(currentPublicLanguage || "en").startsWith("pl")
      ? "pl-PL"
      : "en-US";

    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num) + ` ${getCurrency()}`;
    } catch {
      return `${num.toFixed(2)} ${getCurrency()}`;
    }
  };

  const getCartItemLabel = (item) =>
    t("cart.itemLabel", { amount: formatCurrency(item?.amount) });

  const getAvailablePresetAmounts = () => {
    const min = getMinAmount();
    const max = getMaxAmount();
    return PRESET_AMOUNTS.filter(
      (v) => Number(v) >= min && Number(v) <= max
    );
  };

  // =========================================================================
  // Preview theme derived from voucher templateVersion
  // =========================================================================
  //
  // IMPORTANT TEMPLATE-SPLIT NOTE
  // -----------------------------
  // ShopPage remains voucher-only, so preview theme must be derived through
  // the VOUCHER layout config helper.
  //
  // We intentionally do NOT use package config here.
  //
  const previewTheme = useMemo(() => {
    return getThemeForTemplateVersion(templateVersion);
  }, [templateVersion]);

  // =========================================================================
  // CART totals
  // =========================================================================
  const computeCartTotalAmount = (items) =>
    items.reduce(
      (sum, item) =>
        sum + Number(item.amount || 0) * Number(item.quantity || 0),
      0
    );

  const computeCartTotalItems = (items) =>
    items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const cartTotalAmount = computeCartTotalAmount(cartItems);
  const cartTotalItems = computeCartTotalItems(cartItems);

  // =========================================================================
  // Error helpers
  // =========================================================================
  const dismissError = () => {
    setFormError(null);
    setFieldErrors({});
  };

  const clearErrorIfRelated = (keys = []) => {
    if (!keys || keys.length === 0) return;

    setFieldErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const copy = { ...prev };
      keys.forEach((k) => {
        if (copy[k]) delete copy[k];
      });
      return copy;
    });

    setTimeout(() => {
      setFieldErrors((current) => {
        const hasAny = current && Object.keys(current).length > 0;
        if (!hasAny) setFormError(null);
        return current;
      });
    }, 0);
  };

  // =========================================================================
  // CART actions
  // =========================================================================
  const addCartItem = (amountValue, source = "preset") => {
    const amt = Number(amountValue);
    if (!Number.isFinite(amt) || amt <= 0) return;

    setCartItems((prev) => {
      const id = `${source}-${amt}`;
      const existingIndex = prev.findIndex((item) => item.id === id);

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
        };
        return updated;
      }

      const label = t("cart.itemLabel", { amount: formatCurrency(amt) });
      return [
        ...prev,
        { id, source, amount: amt, quantity: 1, label },
      ];
    });

    clearErrorIfRelated(["amount", "cart"]);
  };

  const incrementCartItem = (id) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
    clearErrorIfRelated(["amount", "cart"]);
  };

  const decrementCartItem = (id) => {
    setCartItems((prev) => {
      const idx = prev.findIndex((item) => item.id === id);
      if (idx === -1) return prev;

      const item = prev[idx];
      if (item.quantity <= 1) {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      }

      const copy = [...prev];
      copy[idx] = { ...item, quantity: item.quantity - 1 };
      return copy;
    });
    clearErrorIfRelated(["amount", "cart"]);
  };

  const clearCart = () => {
    setCartItems([]);
    clearErrorIfRelated(["amount", "cart"]);
  };

  const handlePresetPlus = (value) => addCartItem(value, "preset");
  const handlePresetMinus = (value) =>
    decrementCartItem(`preset-${Number(value)}`);

  // =========================================================================
  // Focus / scroll helpers
  // =========================================================================
  const focusField = (fieldKey) => {
    const scrollTarget = fieldRefMap[fieldKey]?.current;

    if (scrollTarget && typeof scrollTarget.scrollIntoView === "function") {
      scrollTarget.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (fieldKey === "amount" && amountInputRef.current?.focus) {
      setTimeout(() => {
        try {
          amountInputRef.current.focus({ preventScroll: true });
        } catch {
          amountInputRef.current.focus();
        }
      }, 80);
    }

    if (
      (fieldKey === "acceptTerms" || fieldKey === "acceptPrivacy") &&
      fieldRefMap[fieldKey]?.current?.focus
    ) {
      setTimeout(() => {
        try {
          fieldRefMap[fieldKey].current.focus({ preventScroll: true });
        } catch {
          fieldRefMap[fieldKey].current.focus();
        }
      }, 80);
    }
  };

  const showError = ({ message, fieldKey = null, newFieldErrors = null }) => {
    if (newFieldErrors) setFieldErrors(newFieldErrors);
    setFormError(message || t("errors.generic"));

    if (fieldKey) {
      setTimeout(() => focusField(fieldKey), 60);
    } else {
      setTimeout(() => {
        orderColumnRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
        });
      }, 60);
    }
  };

  // =========================================================================
  // Input handlers
  // =========================================================================
  const handleAmountInputChange = (e) => {
    setAmount(e.target.value);
    clearErrorIfRelated(["amount", "cart"]);
  };

  const handleAmountSliderChange = (e) => {
    setAmount(e.target.value);
    clearErrorIfRelated(["amount", "cart"]);
  };

  const handleBuyerEmailChange = (e) => {
    setBuyerEmail(e.target.value);
    clearErrorIfRelated(["buyerEmail"]);
  };

  const handleBuyerNameChange = (e) => {
    setBuyerName(e.target.value);
    clearErrorIfRelated(["buyerName"]);
  };

  const handleRecipientNameChange = (e) => {
    setRecipientName(e.target.value);
    clearErrorIfRelated(["recipientName"]);
  };

  const handleRecipientEmailChange = (e) => {
    setRecipientEmail(e.target.value);
    clearErrorIfRelated(["recipientEmail"]);
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    clearErrorIfRelated(["message"]);
  };

  const handleTermsChange = (e) => {
    setAcceptTerms(e.target.checked);
    clearErrorIfRelated(["acceptTerms"]);
  };

  const handlePrivacyChange = (e) => {
    setAcceptPrivacy(e.target.checked);
    clearErrorIfRelated(["acceptPrivacy"]);
  };

  // =========================================================================
  // Add custom amount to cart
  // =========================================================================
  const handleAddCustomAmount = () => {
    dismissError();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
      showError({
        message: t("errors.invalidCustomAmount"),
        newFieldErrors: { amount: "Invalid amount" },
        fieldKey: "amount",
      });
      return;
    }

    if (
      numericAmount < getMinAmount() ||
      numericAmount > getMaxAmount()
    ) {
      showError({
        message: t("errors.customAmountOutOfRange", { min: getMinAmount(), max: getMaxAmount(), currency: getCurrency() }),
        newFieldErrors: { amount: "Out of range" },
        fieldKey: "amount",
      });
      return;
    }

    addCartItem(numericAmount, "custom");
    setAmount("");
  };

  // =========================================================================
  // CTA readiness visuals (NOT the click gate)
  // =========================================================================
  const readyToPay =
    !submitting &&
    cartTotalAmount > 0 &&
    acceptTerms &&
    acceptPrivacy &&
    isValidEmail(buyerEmail) &&
    String(buyerName || "").trim().length >= 2;

  const isCtaDisabled =
    submitting || !acceptTerms || !acceptPrivacy;
  const isCtaBlockedByConsents =
    !submitting && (!acceptTerms || !acceptPrivacy);

  // =========================================================================
  // Validation (AMOUNT/CART MUST BE FIRST after click)
  // =========================================================================
  const validateBeforeSubmit = () => {
    const errors = {};

    if (!config) {
      return {
        ok: false,
        message: t("errors.configMissing"),
        errors: {},
        firstField: null,
      };
    }

    if (cartTotalAmount <= 0 || cartItems.length === 0) {
      errors.cart = "Empty cart";
      errors.amount = "Cart empty";
      return {
        ok: false,
        message: t("errors.emptyCart"),
        errors,
        firstField: "amount",
      };
    }

    const numericAmount = Number(cartTotalAmount);
    if (!Number.isFinite(numericAmount)) {
      errors.cart = "Invalid sum";
      errors.amount = "Invalid sum";
      return {
        ok: false,
        message: t("errors.invalidCartTotal"),
        errors,
        firstField: "amount",
      };
    }

    if (
      numericAmount < getMinAmount() ||
      numericAmount > getMaxAmount()
    ) {
      errors.cart = "Out of range";
      errors.amount = "Out of range";
      return {
        ok: false,
        message: t("errors.cartOutOfRange", { min: getMinAmount(), max: getMaxAmount(), currency: getCurrency() }),
        errors,
        firstField: "amount",
      };
    }

    if (!isValidEmail(buyerEmail)) errors.buyerEmail = "Invalid email";
    if (!buyerName || buyerName.trim().length < 2) {
      errors.buyerName = "Required";
    }

    if (recipientEmail && String(recipientEmail).trim().length > 0) {
      if (!isValidEmail(recipientEmail)) {
        errors.recipientEmail = "Invalid email";
      }
    }

    if (!acceptTerms) errors.acceptTerms = "Required";
    if (!acceptPrivacy) errors.acceptPrivacy = "Required";

    const firstField =
      errors.buyerEmail
        ? "buyerEmail"
        : errors.buyerName
        ? "buyerName"
        : errors.recipientEmail
        ? "recipientEmail"
        : errors.acceptTerms
        ? "acceptTerms"
        : errors.acceptPrivacy
        ? "acceptPrivacy"
        : null;

    if (Object.keys(errors).length > 0) {
      let message = t("errors.correctHighlightedFields");

      if (errors.buyerEmail) {
        message = t("errors.invalidBuyerEmail");
      } else if (errors.buyerName) {
        message = t("errors.buyerNameRequired");
      } else if (errors.recipientEmail) {
        message = t("errors.invalidRecipientEmail");
      } else if (errors.acceptTerms || errors.acceptPrivacy) {
        message = t("errors.consentsRequired");
      }

      return { ok: false, message, errors, firstField };
    }

    return { ok: true, message: null, errors: {}, firstField: null };
  };

  // =========================================================================
  // Submit handler
  // =========================================================================
  //
  // IMPORTANT TEMPLATE-SPLIT NOTE
  // -----------------------------
  // Even though ShopPage is public voucher-only UI, the selected template
  // metadata still travels to backend and later influences the actual PDF.
  //
  // Therefore we sanitize:
  //   - templateType
  //   - templateVersion
  //
  // against VOUCHER-SIDE public shop catalogs before sending payload.
  //
  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = validateBeforeSubmit();
    if (!result.ok) {
      showError({
        message: result.message,
        newFieldErrors: result.errors,
        fieldKey: result.firstField,
      });
      return;
    }

    dismissError();
    setSubmitting(true);

    try {
      const safeTemplateType = normalizeShopVoucherTemplateType(
        SHOP_SHOW_VOUCHER_TYPE ? templateType : DEFAULT_TEMPLATE_TYPE
      );

      const safeTemplateVersion =
        normalizeShopVoucherTemplateVersion(templateVersion);

      const payload = {
        amount: Number(cartTotalAmount),
        buyerEmail: String(buyerEmail || "").trim(),
        buyerName: String(buyerName || "").trim(),
        recipientName: recipientName || null,
        recipientEmail: recipientEmail || null,
        message: message || null,
        templateType: safeTemplateType,
        templateVersion: safeTemplateVersion,
      };

      const { orderId, redirectUrl } =
        await createOrderAndInitPayment(payload);

      if (!orderId || !redirectUrl) {
        throw new Error(t("errors.missingServerOrderData"));
      }

      window.location.href = redirectUrl;
    } catch (err) {
      console.error("[ShopPage] Error during checkout:", err);
      const backendMsg = extractBackendMessage(err);

      showError({
        message:
          backendMsg ||
          t("errors.paymentInitFailed"),
        newFieldErrors: {},
        fieldKey: null,
      });

      setSubmitting(false);
    }
  };

  // =========================================================================
  // Smooth scrolling helper for hero buttons
  // =========================================================================
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePreviewBackdropClick = (e) => {
    if (e.target.classList.contains("shop-preview-backdrop")) {
      setShowPreview(false);
    }
  };

  const effectivePreviewAmount =
    cartTotalAmount > 0
      ? cartTotalAmount
      : Number(amount) > 0
      ? Number(amount)
      : getMinAmount();

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div
      ref={viewportRef}
      className={
        useGlobalScrollFallback
          ? "shop-page-viewport shop-page-viewport--global-scroll"
          : "shop-page-viewport"
      }
    >
      <div className="shop-page">
        {/* HERO */}
        <header className="shop-hero">
          <div className="shop-hero-header">
            <div className="shop-public-language-row">
              <PublicLanguageDropdown
                value={currentPublicLanguage}
                onChange={handlePublicLanguageChange}
                className="shop-public-language-dropdown"
                style={{ marginBottom: 0 }}
              />
            </div>

            <div className="shop-hero-title-row">
              <div className="shop-logo-pill">
                <img
                  src={logoSrc}
                  alt={salonName}
                  className="shop-logo-img"
                />
              </div>
              <div className="shop-hero-text">
                <h1 className="shop-title">{t("hero.title")}</h1>
                <p className="shop-subtitle">{t("hero.subtitle")}</p>
              </div>
            </div>

            <div className="shop-hero-badges">
              <button
                type="button"
                className="shop-hero-link"
                onClick={() => scrollToSection("product-info")}
              >
                {t("hero.links.productInfo")}
              </button>
              <button
                type="button"
                className="shop-hero-link"
                onClick={() => scrollToSection("merchant-info")}
              >
                {t("hero.links.merchantInfo")}
              </button>
              <button
                type="button"
                className="shop-hero-link"
                onClick={() => scrollToSection("legal-info")}
              >
                {t("hero.links.legalInfo")}
              </button>
            </div>
          </div>
        </header>

        {/* MAIN LAYOUT */}
        <div className="shop-main-shell">
          <main className="shop-main-grid">
            {/* LEFT COLUMN */}
            <section className="shop-info-column" ref={infoColumnRef}>
              <div className="shop-sticky-panel">
                <div className="shop-box" id="product-info">
                  <h2 className="shop-box-title">{t("productInfo.title")}</h2>

                  <div className="shop-product-image-wrapper">
                    <img
                      src={voucherThemeSrc}
                      alt={t("productInfo.imageAlt")}
                      className="shop-product-image"
                    />
                  </div>

                  <ul className="shop-list">
                    <li>{t("productInfo.bullet1")}</li>
                    <li>{t("productInfo.bullet2")}</li>
                    <li>{t("productInfo.bullet3")}</li>
                    <li>{t("productInfo.bullet4")}</li>
                  </ul>
                </div>

                <div className="shop-box" id="how-it-works">
                  <h2 className="shop-box-title">{t("howItWorks.title")}</h2>
                  <ol className="shop-steps-list">
                    <li>{t("howItWorks.step1")}</li>
                    <li>{t("howItWorks.step2")}</li>
                    <li>{t("howItWorks.step3")}</li>
                    <li>{t("howItWorks.step4")}</li>
                  </ol>
                </div>

                <div className="shop-box" id="why-worth">
                  <h2 className="shop-box-title">{t("whyWorth.title")}</h2>
                  <ul className="shop-list">
                    <li>{t("whyWorth.bullet1")}</li>
                    <li>{t("whyWorth.bullet2")}</li>
                    <li>{t("whyWorth.bullet3")}</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* RIGHT COLUMN */}
            <section className="shop-order-column" ref={orderColumnRef}>
              <div className="shop-order-card">
                {loadingConfig && (
                  <div className="shop-order-inner">
                    <h2 className="shop-box-title">
                      {t("order.title")}
                    </h2>
                    <div className="shop-loading">
                      {t("order.loadingConfig")}
                    </div>
                  </div>
                )}

                {!loadingConfig && configError && (
                  <div className="shop-order-inner">
                    <h2 className="shop-box-title">
                      {t("order.title")}
                    </h2>
                    <div className="shop-error">{configError}</div>
                  </div>
                )}

                {!loadingConfig && !configError && (
                  <div className="shop-order-inner">
                    <h2 className="shop-box-title">
                      {t("order.title")}
                    </h2>
                    <p className="shop-box-subtitle">
                      {t("order.subtitlePrefix")}{" "}
                      <strong>
                        {getMinAmount()} {getCurrency()}
                      </strong>{" "}
                      {t("order.to")}{" "}
                      <strong>
                        {getMaxAmount()} {getCurrency()}
                      </strong>
                      .
                    </p>

                    {formError && (
                      <div className="shop-error">{formError}</div>
                    )}

                    <form
                      className="shop-form"
                      onSubmit={handleSubmit}
                      noValidate
                    >
                      {/* Mini cart summary */}
                      <div className="shop-cart-bar">
                        <div className="shop-cart-bar-left">
                          <div
                            className="shop-cart-icon"
                            aria-hidden="true"
                          >
                            <img
                              src={cartIconSrc}
                              alt=""
                              className="shop-cart-icon-img"
                            />
                          </div>
                          <div className="shop-cart-bar-text">
                            <div className="shop-cart-bar-title">
                              {t("cart.title")}
                            </div>
                            <div className="shop-cart-bar-subtitle">
                              {cartTotalItems > 0 ? (
                                <>
                                  {t("cart.summary", {
                                    count: cartTotalItems,
                                    amount: formatCurrency(cartTotalAmount),
                                  })}
                                </>
                              ) : (
                                t("cart.emptySubtitle")
                              )}
                            </div>
                          </div>
                        </div>
                        {cartTotalItems > 0 && (
                          <button
                            type="button"
                            className="shop-cart-clear"
                            onClick={clearCart}
                          >
                            {t("cart.clear")}
                          </button>
                        )}
                      </div>

                      {/* Cart detail list */}
                      {cartTotalItems > 0 && (
                        <div className="shop-cart-items">
                          {cartItems.map((item) => (
                            <div
                              key={item.id}
                              className="shop-cart-item-row"
                            >
                              <div className="shop-cart-item-main">
                                <div className="shop-cart-item-label">
                                  {getCartItemLabel(item)}
                                </div>
                                <div className="shop-cart-item-price">
                                  {t("cart.denomination", {
                                    amount: formatCurrency(item.amount),
                                  })}
                                </div>
                              </div>
                              <div className="shop-cart-item-controls">
                                <button
                                  type="button"
                                  className="shop-cart-item-btn"
                                  onClick={() =>
                                    decrementCartItem(item.id)
                                  }
                                  aria-label={t("cart.decreaseVoucherCount")}
                                >
                                  −
                                </button>
                                <span className="shop-cart-item-qty">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  className="shop-cart-item-btn"
                                  onClick={() =>
                                    incrementCartItem(item.id)
                                  }
                                  aria-label={t("cart.increaseVoucherCount")}
                                >
                                  +
                                </button>
                              </div>
                              <div className="shop-cart-item-total">
                                {formatCurrency(
                                  item.amount * item.quantity
                                )}
                              </div>
                            </div>
                          ))}

                          <div className="shop-cart-summary-row">
                            <span>{t("cart.totalVoucherValue")}</span>
                            <span className="shop-cart-summary-amount">
                              {formatCurrency(cartTotalAmount)}
                            </span>
                          </div>

                          <small className="shop-help">
                            {t("cart.help")}
                          </small>
                        </div>
                      )}

                      {/* Preset amounts */}
                      <div className="shop-field">
                        <label className="shop-label">
                          {t("amount.presetLabelPrefix")} {" "}
                          <strong>+-</strong>
                        </label>
                        <div className="shop-denominations-row">
                          {getAvailablePresetAmounts().map((value) => {
                            const existing =
                              cartItems.find(
                                (item) =>
                                  item.source === "preset" &&
                                  Number(item.amount) === Number(value)
                              ) || null;

                            const qty = existing?.quantity || 0;

                            return (
                              <div
                                key={value}
                                className="shop-denomination-card"
                              >
                                <div className="shop-denomination-amount">
                                  {formatCurrency(value)}
                                </div>
                                <div className="shop-denomination-qty-row">
                                  <button
                                    type="button"
                                    className="shop-denomination-btn"
                                    onClick={() =>
                                      handlePresetMinus(value)
                                    }
                                    disabled={qty === 0}
                                    aria-label={t("cart.removeFromCart")}
                                  >
                                    −
                                  </button>
                                  <span className="shop-denomination-qty">
                                    {qty}
                                  </span>
                                  <button
                                    type="button"
                                    className="shop-denomination-btn"
                                    onClick={() =>
                                      handlePresetPlus(value)
                                    }
                                    aria-label={t("cart.addToCart")}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <small className="shop-help">
                          {t("amount.presetHelp")}
                        </small>

                        {/* ------------------------------------------------------------------
                           UX tweak (requested):
                           Move the "custom amount" hint here so the user reads it immediately
                           after the preset denominations explanation (better flow).
                        ------------------------------------------------------------------- */}
                        <small
                          className="shop-help"
                          style={{ marginTop: "10px", display: "block" }}
                        >
                          {t("amount.customHint")}
                        </small>
                      </div>

                      {/* Custom amount section */}
                      <div className="shop-field" ref={amountSectionRef}>
                        <label
                          htmlFor="amount"
                          className="shop-label"
                        >
                          {t("amount.customLabel", { currency: getCurrency() })}
                        </label>

                        <div className="shop-amount-row">
                          <input
                            id="amount"
                            ref={amountInputRef}
                            type="number"
                            min={getMinAmount()}
                            max={getMaxAmount()}
                            step={getStep()}
                            className={
                              fieldErrors.amount
                                ? "shop-input shop-input-amount shop-input--error"
                                : "shop-input shop-input-amount"
                            }
                            value={amount}
                            onChange={handleAmountInputChange}
                            placeholder={`${getMinAmount()} – ${getMaxAmount()} ${getCurrency()}`}
                            inputMode="numeric"
                          />
                          <div className="shop-amount-display">
                            {formatCurrency(amount)}
                          </div>
                        </div>

                        <input
                          type="range"
                          min={getMinAmount()}
                          max={getMaxAmount()}
                          step={getStep()}
                          value={amount || getMinAmount()}
                          onChange={handleAmountSliderChange}
                          className="shop-slider"
                        />

                        <div className="shop-slider-scale">
                          <span>{getMinAmount()}</span>
                          <span>{t("amount.average")}</span>
                          <span>{getMaxAmount()}</span>
                        </div>

                        <button
                          type="button"
                          className="shop-secondary-button shop-add-custom-button"
                          onClick={handleAddCustomAmount}
                        >
                          {t("amount.addCustom")}
                        </button>
                      </div>

                      {/* Voucher type (feature-flagged) */}
                      {SHOP_SHOW_VOUCHER_TYPE ? (
                        <div className="shop-field">
                          <label className="shop-label">
                            {t("templateType.title")}
                          </label>
                          <div className="shop-template-type-row">
                            <button
                              type="button"
                              className={
                                templateType === "A4_ON_STATIONERY"
                                  ? "shop-type-card shop-type-card--active"
                                  : "shop-type-card"
                              }
                              onClick={() =>
                                setTemplateType("A4_ON_STATIONERY")
                              }
                            >
                              <div className="shop-type-title">
                                {t("templateType.stationery.title")}
                              </div>
                              <div className="shop-type-desc">
                                {t("templateType.stationery.description")}
                              </div>
                            </button>

                            <button
                              type="button"
                              className={
                                templateType === "A4_ENVELOPE"
                                  ? "shop-type-card shop-type-card--active"
                                  : "shop-type-card"
                              }
                              onClick={() =>
                                setTemplateType("A4_ENVELOPE")
                              }
                            >
                              <div className="shop-type-title">
                                {t("templateType.envelope.title")}
                              </div>
                              <div className="shop-type-desc">
                                {t("templateType.envelope.description")}
                              </div>
                            </button>

                            <button
                              type="button"
                              className={
                                templateType === "A4_COMBINED"
                                  ? "shop-type-card shop-type-card--active"
                                  : "shop-type-card"
                              }
                              onClick={() =>
                                setTemplateType("A4_COMBINED")
                              }
                            >
                              <div className="shop-type-title">
                                {t("templateType.combined.title")}
                              </div>
                              <div className="shop-type-desc">
                                {t("templateType.combined.description")}
                              </div>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="shop-field shop-field--compact"
                          aria-hidden="true"
                        >
                          <small className="shop-help">
                            {t("templateType.hiddenPrefix")}{" "}
                            <strong>{t("templateType.stationery.title")}</strong>{" "}
                            {t("templateType.hiddenSuffix")}
                          </small>
                        </div>
                      )}

                      {/* Theme + preview */}
                      <div className="shop-field">
                        <label className="shop-label">
                          {t("theme.title")}
                        </label>
                        <div className="shop-theme-row">
                          <button
                            type="button"
                            className={
                              templateVersion === "company"
                                ? "shop-theme-pill shop-theme-pill--active"
                                : "shop-theme-pill"
                            }
                            onClick={() =>
                              setTemplateVersion("company")
                            }
                          >
                            {t("theme.company")}
                          </button>
                          <button
                            type="button"
                            className={
                              templateVersion === "christmas"
                                ? "shop-theme-pill shop-theme-pill--active"
                                : "shop-theme-pill"
                            }
                            onClick={() =>
                              setTemplateVersion("christmas")
                            }
                          >
                            {t("theme.christmas")}
                          </button>
                          <button
                            type="button"
                            className={
                              templateVersion === "valentine"
                                ? "shop-theme-pill shop-theme-pill--active"
                                : "shop-theme-pill"
                            }
                            onClick={() =>
                              setTemplateVersion("valentine")
                            }
                          >
                            {t("theme.valentine")}
                          </button>
                        </div>

                        <button
                          type="button"
                          className="shop-secondary-button"
                          onClick={() => setShowPreview(true)}
                        >
                          {t("preview.button")}
                        </button>
                      </div>

                      {/* Buyer email */}
                      <div className="shop-field">
                        <label
                          htmlFor="buyerEmail"
                          className="shop-label"
                        >
                          {t("form.buyerEmail")}
                        </label>
                        <input
                          id="buyerEmail"
                          ref={buyerEmailRef}
                          type="email"
                          className={
                            fieldErrors.buyerEmail
                              ? "shop-input shop-input--error"
                              : "shop-input"
                          }
                          value={buyerEmail}
                          onChange={handleBuyerEmailChange}
                          placeholder={t("form.buyerEmailPlaceholder")}
                          required
                          autoComplete="email"
                        />
                      </div>

                      {/* Buyer name */}
                      <div className="shop-field">
                        <label
                          htmlFor="buyerName"
                          className="shop-label"
                        >
                          {t("form.buyerName")}
                        </label>
                        <input
                          id="buyerName"
                          ref={buyerNameRef}
                          type="text"
                          className={
                            fieldErrors.buyerName
                              ? "shop-input shop-input--error"
                              : "shop-input"
                          }
                          value={buyerName}
                          onChange={handleBuyerNameChange}
                          placeholder={t("form.buyerNamePlaceholder")}
                          required
                          autoComplete="name"
                        />
                      </div>

                      {/* Recipient */}
                      <div className="shop-field">
                        <label
                          htmlFor="recipientName"
                          className="shop-label"
                        >
                          {t("form.recipientName")}
                        </label>
                        <input
                          id="recipientName"
                          type="text"
                          className="shop-input"
                          value={recipientName}
                          onChange={handleRecipientNameChange}
                          placeholder={t("form.recipientNamePlaceholder")}
                        />
                      </div>

                      <div className="shop-field">
                        <label
                          htmlFor="recipientEmail"
                          className="shop-label"
                        >
                          {t("form.recipientEmail")}
                        </label>
                        <input
                          id="recipientEmail"
                          ref={recipientEmailRef}
                          type="email"
                          className={
                            fieldErrors.recipientEmail
                              ? "shop-input shop-input--error"
                              : "shop-input"
                          }
                          value={recipientEmail}
                          onChange={handleRecipientEmailChange}
                          placeholder={t("form.recipientEmailPlaceholder")}
                          autoComplete="email"
                        />
                        <small className="shop-help">
                          {t("form.recipientEmailHelp")}
                        </small>
                      </div>

                      {/* Message */}
                      <div className="shop-field">
                        <label htmlFor="message" className="shop-label">
                          {t("form.message")}
                        </label>
                        <textarea
                          id="message"
                          className="shop-textarea"
                          rows={3}
                          maxLength={300}
                          value={message}
                          onChange={handleMessageChange}
                          placeholder={t("form.messagePlaceholder")}
                        />
                        <div className="shop-char-counter">
                          {message.length}/300
                        </div>
                      </div>

                      {/* Accordions */}
                      <div
                        className="shop-accordion"
                        id="merchant-info"
                      >
                        <button
                          type="button"
                          className="shop-accordion-header"
                          onClick={() =>
                            setShowMerchantInfo((open) => !open)
                          }
                        >
                          <span className="shop-accordion-title">
                            {t("merchant.title")}
                          </span>
                          <span
                            className={
                              "shop-accordion-icon" +
                              (showMerchantInfo
                                ? " shop-accordion-icon--open"
                                : "")
                            }
                          >
                            ▼
                          </span>
                        </button>
                        {showMerchantInfo && (
                          <div className="shop-accordion-body">
                            <p className="shop-merchant-name">
                              Health &amp; Beauty Dorota Rybacka
                            </p>
                            <p className="shop-merchant-line">
                              ul. 3 Maja 74, 62-500 Konin
                            </p>
                            <p className="shop-merchant-line">
                              NIP: 6652721214
                            </p>
                            <p className="shop-merchant-line">
                              {t("merchant.emailLabel")}{" "}
                              <a href="mailto:kontakt@hb.konin.pl">
                                kontakt@hb.konin.pl
                              </a>
                            </p>
                            <p className="shop-merchant-line">
                              {t("merchant.phoneLabel")}{" "}
                              <a href="tel:+48786962370">
                                +48 786 962 370
                              </a>
                            </p>
                          </div>
                        )}
                      </div>

                      <div
                        className="shop-accordion"
                        id="payment-info"
                      >
                        <button
                          type="button"
                          className="shop-accordion-header"
                          onClick={() =>
                            setShowPaymentInfo((open) => !open)
                          }
                        >
                          <span className="shop-accordion-title">
                            {t("payment.title")}
                          </span>
                          <span
                            className={
                              "shop-accordion-icon" +
                              (showPaymentInfo
                                ? " shop-accordion-icon--open"
                                : "")
                            }
                          >
                            ▼
                          </span>
                        </button>
                        {showPaymentInfo && (
                          <div className="shop-accordion-body">
                            <ul className="shop-list">
                              <li>{t("payment.bullet1")}</li>
                              <li>{t("payment.bullet2")}</li>
                              <li>{t("payment.bullet3")}</li>
                            </ul>
                            <div className="shop-payment-logos">
                              <span className="shop-payment-logo">
                                Przelewy24
                              </span>
                              <span className="shop-payment-logo">
                                Visa
                              </span>
                              <span className="shop-payment-logo">
                                Mastercard
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="shop-accordion" id="legal-info">
                        <button
                          type="button"
                          className="shop-accordion-header"
                          onClick={() =>
                            setShowLegalInfo((open) => !open)
                          }
                        >
                          <span className="shop-accordion-title">
                            {t("legal.title")}
                          </span>
                          <span
                            className={
                              "shop-accordion-icon" +
                              (showLegalInfo
                                ? " shop-accordion-icon--open"
                                : "")
                            }
                          >
                            ▼
                          </span>
                        </button>
                        {showLegalInfo && (
                          <div className="shop-accordion-body">
                            <h3 className="shop-legal-subtitle">
                              {t("legal.withdrawalTitle")}
                            </h3>
                            <p className="shop-legal-text">
                              {t("legal.withdrawalPrefix")}{" "}
                              <a
                                href={termsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("links.termsIn")}
                              </a>
                              {t("common.period")}
                            </p>

                            <h3 className="shop-legal-subtitle">
                              {t("legal.complaintsTitle")}
                            </h3>
                            <p className="shop-legal-text">
                              {t("legal.complaintsPrefix")}{" "}
                              <a
                                href={termsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("links.termsIn")}
                              </a>
                              {t("legal.complaintsSuffix")}
                            </p>

                            <h3 className="shop-legal-subtitle">
                              {t("legal.termsPrivacyTitle")}
                            </h3>
                            <p className="shop-legal-text">
                              {t("legal.readBeforePurchase")}
                            </p>
                            <ul className="shop-list">
                              <li>
                                <a
                                  href={termsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("links.termsInstrumental")}
                                </a>
                              </li>
                              <li>
                                <a
                                  href={privacyPolicyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("links.privacyInstrumental")}
                                </a>
                              </li>
                            </ul>
                            <p className="shop-legal-text">
                              {t("legal.documentsDescription")}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Consents */}
                      <div className="shop-field shop-field-checkbox">
                        <div className="shop-consent">
                          <label
                            htmlFor="shop-terms"
                            className="shop-consent-label"
                          >
                            <input
                              id="shop-terms"
                              ref={acceptTermsRef}
                              type="checkbox"
                              checked={acceptTerms}
                              onChange={handleTermsChange}
                            />
                            <span className="shop-consent-text">
                              {t("consents.termsPrefix")}{" "}
                              <a
                                href={termsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("links.termsInstrumental")}
                              </a>{" "}
                              {t("consents.termsSuffix")}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="shop-field shop-field-checkbox">
                        <div className="shop-consent">
                          <label
                            htmlFor="shop-privacy"
                            className="shop-consent-label"
                          >
                            <input
                              id="shop-privacy"
                              ref={acceptPrivacyRef}
                              type="checkbox"
                              checked={acceptPrivacy}
                              onChange={handlePrivacyChange}
                            />
                            <span className="shop-consent-text">
                              {t("consents.privacyPrefix")}{" "}
                              <a
                                href={privacyPolicyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("links.privacyInstrumental")}
                              </a>{" "}
                              {t("consents.privacySuffix")}
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="shop-actions">
                        <button
                          type="submit"
                          className={
                            readyToPay
                              ? "shop-primary-button"
                              : "shop-primary-button shop-primary-button--inactive"
                          }
                          disabled={isCtaDisabled}
                          aria-disabled={isCtaDisabled ? "true" : "false"}
                        >
                          {submitting
                            ? t("cta.redirecting")
                            : t("cta.pay")}
                        </button>

                        <p className="shop-small-note">
                          {t("cta.note")}
                        </p>

                        {isCtaBlockedByConsents && (
                          <p className="shop-cta-hint" role="note">
                            {t("cta.consentHint")}
                          </p>
                        )}
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Sticky bottom error toast (MOBILE) */}
      {formError && (
        <div
          className="shop-error-toast"
          role="alert"
          aria-live="polite"
        >
          <div className="shop-error-toast__inner">
            <div className="shop-error-toast__text">{formError}</div>
            <button
              type="button"
              className="shop-error-toast__close"
              onClick={dismissError}
              aria-label={t("common.closeMessage")}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {showPreview && (
        <div
          className="shop-preview-backdrop"
          onClick={handlePreviewBackdropClick}
        >
          <div
            className="shop-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="shop-preview-close"
              onClick={() => setShowPreview(false)}
              aria-label={t("preview.closeAria")}
            >
              ×
            </button>

            <h2 className="shop-preview-title">{t("preview.button")}</h2>
            <p className="shop-preview-subtitle">
              {t("preview.subtitle")}
            </p>

            <div className="shop-preview-voucher-wrapper">
              <VoucherPreview
                preview={{
                  // IMPORTANT:
                  // ShopPage preview remains voucher-family preview.
                  businessMode: "voucher",

                  theme: previewTheme,
                  salonName,
                  logoUrl: logoSrc,
                  amount: effectivePreviewAmount,
                  currency: getCurrency(),
                  recipientName,
                  buyerName,
                  message,

                  // The selected layout metadata that later also goes to backend.
                  templateType: normalizeShopVoucherTemplateType(
                    SHOP_SHOW_VOUCHER_TYPE
                      ? templateType
                      : DEFAULT_TEMPLATE_TYPE
                  ),
                  templateVersion:
                    normalizeShopVoucherTemplateVersion(templateVersion),

                  voucherNumberPlaceholder: t("preview.voucherNumberPlaceholder"),
                }}
              />
            </div>

            <button
              type="button"
              className="shop-secondary-button shop-preview-ok"
              onClick={() => setShowPreview(false)}
            >
              {t("preview.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;

